import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import api from "@/lib/api";
import { Avatar } from "@/components/common";
import { useLeaveTypes } from "@/context/LeaveTypesContext";
import { Popover, PopoverTrigger, PopoverContent } from "@/components/ui/popover";
import { Users, ArrowRight, ChevronLeft, ChevronRight, CalendarDays } from "lucide-react";

const iso = (d) => `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, "0")}-${String(d.getDate()).padStart(2, "0")}`;
const SHORT = ["Jan", "Feb", "Mar", "Apr", "May", "Jun", "Jul", "Aug", "Sep", "Oct", "Nov", "Dec"];
const WD = ["Sun", "Mon", "Tue", "Wed", "Thu", "Fri", "Sat"];
const fmtShort = (s) => { try { const d = new Date(s.slice(0, 10)); return `${d.getDate()} ${SHORT[d.getMonth()]}`; } catch { return s; } };

// The specific working days within a booking that fall in the viewed week.
const awayDays = (b, schedule, weekStart, weekEnd) => {
  const sched = schedule?.[b.employee_id] || {};
  const out = [];
  const s = new Date(b.start_date.slice(0, 10) + "T00:00:00");
  const e = new Date(b.end_date.slice(0, 10) + "T00:00:00");
  const from = new Date(Math.max(s, new Date(weekStart + "T00:00:00")));
  const to = new Date(Math.min(e, new Date(weekEnd + "T00:00:00")));
  for (let d = new Date(from); d <= to; d.setDate(d.getDate() + 1)) {
    const key = iso(d);
    if (sched[key] == null || sched[key] <= 0) continue; // not a working day for them
    const isStart = key === b.start_date.slice(0, 10);
    const isEnd = key === b.end_date.slice(0, 10);
    const half = b.duration_type === "half" || (isStart && b.half_day_start) || (isEnd && b.half_day_end);
    out.push({ key, label: `${WD[d.getDay()]} ${d.getDate()} ${SHORT[d.getMonth()]}`, half });
  }
  return out;
};

// A compact "who's off in my team this week" strip for the employee homepage.
export default function TeamOffStrip({ empId }) {
  const [items, setItems] = useState(null);
  const [schedule, setSchedule] = useState({});
  const [range, setRange] = useState({ start: "", end: "" });
  const [weekLabel, setWeekLabel] = useState("");
  const [offset, setOffset] = useState(0); // 0 = this week, 1 = next week, …
  const { map: ltMap } = useLeaveTypes();

  useEffect(() => {
    let alive = true;
    (async () => {
      const now = new Date();
      const day = (now.getDay() + 6) % 7; // Mon = 0
      const mon = new Date(now.getFullYear(), now.getMonth(), now.getDate() - day + offset * 7);
      const sun = new Date(mon.getFullYear(), mon.getMonth(), mon.getDate() + 6);
      const start = iso(mon), end = iso(sun);
      if (alive) { setWeekLabel(`${fmtShort(start)} – ${fmtShort(end)}`); setRange({ start, end }); }
      const params = { start, end };
      try {
        const me = await api.get(`/employees/${empId}`);
        if (me.data?.manager_id) params.manager_id = me.data.manager_id;
        else if (me.data?.department) params.department = me.data.department;
      } catch { /* fall back to org-wide */ }
      try {
        const { data } = await api.get("/calendar", { params });
        if (!alive) return;
        const rows = (data.bookings || [])
          .filter((b) => b.employee_id !== empId && ["approved", "pending"].includes(b.status))
          .sort((a, b) => (a.start_date < b.start_date ? -1 : 1));
        setSchedule(data.schedule || {});
        setItems(rows);
      } catch { if (alive) { setItems([]); setSchedule({}); } }
    })();
    return () => { alive = false; };
  }, [empId, offset]);

  if (items === null) return null;

  const heading = offset === 0 ? "Who's off in my team this week" : offset === 1 ? "Who's off in my team next week" : "Who's off in my team";

  return (
    <div className="bg-white border border-slate-200 rounded-2xl shadow-sm p-5" data-testid="team-off-strip">
      <div className="flex items-center justify-between gap-3 mb-3">
        <div className="flex items-center gap-2">
          <span className="rounded-lg bg-[#EFF8FF] p-2 text-[#2EA3FD]"><Users size={16} /></span>
          <div>
            <h2 className="font-semibold text-[#0B1A2F] leading-tight">{heading}</h2>
            <p className="text-xs text-slate-400">{weekLabel}</p>
          </div>
        </div>
        <div className="flex items-center gap-2 shrink-0">
          <div className="flex items-center rounded-lg border border-slate-200 overflow-hidden">
            <button data-testid="team-off-prev-week" onClick={() => setOffset((o) => Math.max(0, o - 1))} disabled={offset === 0}
              className="px-2 py-1.5 text-slate-500 hover:bg-slate-50 disabled:opacity-30 disabled:cursor-not-allowed" aria-label="Previous week"><ChevronLeft size={15} /></button>
            <button data-testid="team-off-this-week" onClick={() => setOffset(0)} disabled={offset === 0}
              className="px-2 py-1.5 text-[11px] font-medium text-slate-500 hover:bg-slate-50 disabled:opacity-30 border-x border-slate-200">This week</button>
            <button data-testid="team-off-next-week" onClick={() => setOffset((o) => o + 1)}
              className="px-2 py-1.5 text-slate-500 hover:bg-slate-50" aria-label="Next week"><ChevronRight size={15} /></button>
          </div>
          <Link to="/calendar" data-testid="team-off-calendar-link" className="text-xs font-medium text-[#2EA3FD] hover:underline inline-flex items-center gap-1">Team calendar<ArrowRight size={13} /></Link>
        </div>
      </div>
      {items.length === 0 ? (
        <p className="text-sm text-slate-400 py-2" data-testid="team-off-empty">{offset === 0 ? "No one else in your team is off this week — full coverage." : "No one else in your team is off that week."}</p>
      ) : (
        <div className="flex gap-2.5 overflow-x-auto cass-scroll pb-1">
          {items.map((b) => {
            const color = b.status === "approved" ? (ltMap[b.leave_type]?.color || "#2EA3FD") : "#F59E0B";
            const name = ltMap[b.leave_type]?.name || b.leave_type;
            const days = awayDays(b, schedule, range.start, range.end);
            return (
              <Popover key={b.id}>
                <PopoverTrigger asChild>
                  <button data-testid={`team-off-${b.id}`} className="shrink-0 min-w-[150px] text-left rounded-xl border border-slate-200 bg-slate-50/60 px-3 py-2.5 hover:border-[#2EA3FD] hover:bg-[#EFF8FF]/40 transition-colors cursor-pointer focus:outline-none focus:ring-2 focus:ring-[#2EA3FD]/40">
                    <div className="flex items-center gap-2">
                      <Avatar name={b.employee_name} photo={b.employee_photo} color={color} size={30} />
                      <div className="min-w-0">
                        <p className="text-sm font-medium text-[#0B1A2F] truncate">{b.employee_name}</p>
                        <p className="text-[11px] text-slate-500 truncate">{fmtShort(b.start_date)}{b.start_date !== b.end_date ? ` – ${fmtShort(b.end_date)}` : ""}</p>
                      </div>
                    </div>
                    <div className="flex items-center gap-1.5 mt-2">
                      <span className="h-2 w-2 rounded-full shrink-0" style={{ backgroundColor: color }} />
                      <span className="text-[11px] text-slate-500 truncate">{b.status === "approved" ? name : "Pending approval"}</span>
                    </div>
                  </button>
                </PopoverTrigger>
                <PopoverContent align="start" className="w-64 p-0" data-testid={`team-off-popover-${b.id}`}>
                  <div className="flex items-center gap-2 px-3 py-2.5 border-b border-slate-100">
                    <Avatar name={b.employee_name} photo={b.employee_photo} color={color} size={30} />
                    <div className="min-w-0">
                      <p className="text-sm font-semibold text-[#0B1A2F] truncate">{b.employee_name}</p>
                      <p className="text-[11px] text-slate-500 truncate flex items-center gap-1"><span className="h-2 w-2 rounded-full" style={{ backgroundColor: color }} />{b.status === "approved" ? name : "Pending approval"}</p>
                    </div>
                  </div>
                  <div className="px-3 py-2.5">
                    <p className="text-[11px] font-medium text-slate-400 mb-1.5 flex items-center gap-1"><CalendarDays size={12} />Away these days</p>
                    {days.length === 0 ? (
                      <p className="text-xs text-slate-400">{fmtShort(b.start_date)}{b.start_date !== b.end_date ? ` – ${fmtShort(b.end_date)}` : ""} (no working days this week)</p>
                    ) : (
                      <ul className="space-y-1">
                        {days.map((d) => (
                          <li key={d.key} data-testid={`team-off-day-${b.id}-${d.key}`} className="flex items-center justify-between text-xs">
                            <span className="text-slate-700">{d.label}</span>
                            <span className={`text-[10px] rounded-full px-2 py-0.5 ${d.half ? "bg-amber-100 text-amber-700" : "bg-slate-100 text-slate-500"}`}>{d.half ? "Half day" : "Full day"}</span>
                          </li>
                        ))}
                      </ul>
                    )}
                  </div>
                </PopoverContent>
              </Popover>
            );
          })}
        </div>
      )}
    </div>
  );
}
